import * as React from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { Link as RouterLink } from 'react-router-dom';
import { AI_LABELS, getAiHealth, getAiUsageSummary, getAiWorkflowOverview } from '../../Api';
import { AiUsageSummaryDto, AiWorkflowOverviewDto, DashboardAiHealthDto } from '../../models/dtos/AiDto';
import AiHealthPanels from '../../components/ai/AiHealthPanels';
import { CostBarChart, usageRowLabel } from '../../components/ai/AiUsageCharts';
import {
    aiDayRange, aiLoadErrorText, aiUtcDay, formatAiCount, formatAiUsd, KpiCard,
} from '../../components/ai/AiShared';

const SUMMARY_LABELS = {
    today: 'ai-usage-today',
    week: 'ai-usage-7d',
    month: 'ai-usage-30d',
    daily: 'ai-usage-daily',
    callers: 'ai-usage-callers',
    targets: 'ai-usage-targets',
};

function totalsLines(summary: AiUsageSummaryDto | null): Array<[string, React.ReactNode]> {
    const t = summary?.totals;
    return [
        ['Calls', formatAiCount(t?.calls)],
        ['Errors', formatAiCount(t?.errors)],
        ['Blocked', formatAiCount(t?.blocked)],
        ['Input tokens', formatAiCount(t?.inputTokens)],
        ['Output tokens', formatAiCount(t?.outputTokens)],
    ];
}

export default function AiOverviewPage() {
    const [health, setHealth] = React.useState<DashboardAiHealthDto | null>(null);
    const [healthError, setHealthError] = React.useState<string | null>(null);
    const [today, setToday] = React.useState<AiUsageSummaryDto | null>(null);
    const [week, setWeek] = React.useState<AiUsageSummaryDto | null>(null);
    const [month, setMonth] = React.useState<AiUsageSummaryDto | null>(null);
    const [daily, setDaily] = React.useState<AiUsageSummaryDto | null>(null);
    const [callers, setCallers] = React.useState<AiUsageSummaryDto | null>(null);
    const [targets, setTargets] = React.useState<AiUsageSummaryDto | null>(null);
    const [workflows, setWorkflows] = React.useState<AiWorkflowOverviewDto | null>(null);
    const [usageError, setUsageError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const to = aiUtcDay(0);
        const from30 = aiUtcDay(29);
        getAiWorkflowOverview(30, 6).then(setWorkflows);
        const [h, t, w, m, d, c, tg] = await Promise.all([
            getAiHealth(),
            getAiUsageSummary(to, to, [], SUMMARY_LABELS.today),
            getAiUsageSummary(aiUtcDay(6), to, [], SUMMARY_LABELS.week),
            getAiUsageSummary(from30, to, [], SUMMARY_LABELS.month),
            getAiUsageSummary(from30, to, ['day'], SUMMARY_LABELS.daily),
            getAiUsageSummary(from30, to, ['caller'], SUMMARY_LABELS.callers),
            getAiUsageSummary(from30, to, ['task', 'agent'], SUMMARY_LABELS.targets),
        ]);
        setLoading(false);
        setHealth(h);
        setHealthError(h == null ? aiLoadErrorText(AI_LABELS.health, 'Failed to load AI health.') : null);
        setToday(t);
        setWeek(w);
        setMonth(m);
        setDaily(d);
        setCallers(c);
        setTargets(tg);
        const failed = Object.entries({ today: t, week: w, month: m, daily: d, callers: c, targets: tg })
            .filter(([, v]) => v == null)
            .map(([k]) => k as keyof typeof SUMMARY_LABELS);
        setUsageError(failed.length > 0 ? aiLoadErrorText(SUMMARY_LABELS[failed[0]], 'Failed to load usage.') : null);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const days = aiDayRange(aiUtcDay(29), aiUtcDay(0));
    const costByDay = new Map<string, number>();
    (daily?.rows ?? []).forEach(r => { if (r.day) costByDay.set(r.day, (costByDay.get(r.day) ?? 0) + r.costUsd); });
    const callsByDay = new Map<string, number>();
    (daily?.rows ?? []).forEach(r => { if (r.day) callsByDay.set(r.day, (callsByDay.get(r.day) ?? 0) + r.calls); });

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI overview</Typography>
                    <Typography variant="body2" color="text.secondary">
                        betup-server-ai: health of provider keys, MCP servers, budgets, tasks and agents, and spend (UTC days).
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>

            <Box>
                <Typography variant="h5" sx={{ mb: 1.5 }}>Health</Typography>
                {loading && health == null ? <Typography color="text.secondary">Loading…</Typography> : (
                    <AiHealthPanels health={health} loadError={healthError} />
                )}
            </Box>

            <Box>
                <Typography variant="h5" sx={{ mb: 1.5 }}>Spend</Typography>
                {usageError && <Alert severity="error" sx={{ mb: 2 }}>{usageError}</Alert>}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}><KpiCard title="Today" value={formatAiUsd(today?.totals.costUsd)} lines={totalsLines(today)} /></Grid>
                    <Grid item xs={12} md={4}><KpiCard title="Last 7 days" value={formatAiUsd(week?.totals.costUsd)} lines={totalsLines(week)} /></Grid>
                    <Grid item xs={12} md={4}><KpiCard title="Last 30 days" value={formatAiUsd(month?.totals.costUsd)} lines={totalsLines(month)} /></Grid>
                </Grid>
            </Box>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">Daily cost (30 days)</Typography>
                    <LineChart
                        xAxis={[{ scaleType: 'point', data: days, valueFormatter: (d: string) => d.slice(5) }]}
                        yAxis={[{ id: 'cost', valueFormatter: (v: number) => formatAiUsd(v) }, { id: 'calls' }]}
                        rightAxis="calls"
                        leftAxis="cost"
                        series={[
                            { data: days.map(d => costByDay.get(d) ?? 0), label: 'Cost (USD)', yAxisKey: 'cost', area: true, valueFormatter: (v: number | null) => formatAiUsd(v), showMark: false },
                            { data: days.map(d => callsByDay.get(d) ?? 0), label: 'Calls', yAxisKey: 'calls', showMark: false },
                        ]}
                        height={300}
                        margin={{ left: 80, right: 60 }}
                    />
                </CardContent>
            </Card>

            <WorkflowSpend overview={workflows} />

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold">Cost by caller (30 days)</Typography>
                            <CostBarChart rows={callers?.rows ?? []} label={r => r.caller ?? '(none)'} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold">Cost by task / agent (30 days)</Typography>
                            <CostBarChart rows={targets?.rows ?? []} label={usageRowLabel} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Stack>
    );
}

/** The most expensive workflows of the last 30 days (the rest of the hub as unassigned) and their daily spend. */
function WorkflowSpend({ overview }: { overview: AiWorkflowOverviewDto | null }) {
    if (overview == null) return null;
    const ranked = [
        ...overview.workflows.map(w => ({ label: w.name, code: w.code, cost: w.costUsd, runs: w.runs })),
        ...(overview.unassignedUsd > 0 ? [{ label: 'Not in a workflow', code: '', cost: overview.unassignedUsd, runs: 0 }] : []),
    ];
    const days = overview.daily.map(d => d.day);
    const longest = ranked.reduce((max, r) => Math.max(max, r.label.length), 0);
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight="bold">Most expensive workflows (30 days)</Typography>
                            <Button size="small" component={RouterLink} to="/ai/workflows">All workflows</Button>
                        </Stack>
                        {ranked.length === 0 ? <Typography variant="body2" color="text.secondary">No usage in this period.</Typography> : (
                            <BarChart
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: ranked.map(r => r.label) }]}
                                xAxis={[{ valueFormatter: (v: number) => formatAiUsd(v) }]}
                                series={[{ data: ranked.map(r => r.cost), label: 'Cost (USD)', valueFormatter: (v: number | null) => formatAiUsd(v) }]}
                                height={Math.max(180, 40 + ranked.length * 34)}
                                margin={{ left: Math.min(260, 20 + longest * 7), right: 40, top: 20, bottom: 30 }}
                                slotProps={{ legend: { hidden: true } }}
                            />
                        )}
                        <Stack gap={0.5} sx={{ mt: 1 }}>
                            {overview.workflows.map(w => (
                                <Typography key={w.code} variant="caption" color="text.secondary">
                                    <RouterLink to={`/ai/workflows/${encodeURIComponent(w.code)}`}>{w.name}</RouterLink>
                                    {` — ${formatAiUsd(w.costUsd)}, ${formatAiCount(w.runs)} runs`}
                                    {w.runs > 0 ? `, ${formatAiUsd(w.costUsd / w.runs)} per run` : ''}
                                </Typography>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">Daily cost by workflow</Typography>
                        {days.length === 0 ? <Typography variant="body2" color="text.secondary">No usage in this period.</Typography> : (
                            <BarChart
                                xAxis={[{ scaleType: 'band', data: days, valueFormatter: (d: string) => d.slice(5) }]}
                                yAxis={[{ valueFormatter: (v: number) => formatAiUsd(v) }]}
                                series={[
                                    ...overview.workflows.map(w => ({
                                        data: overview.daily.map(d => d.byWorkflow[w.code] ?? 0), label: w.name, stack: 'cost',
                                        valueFormatter: (v: number | null) => formatAiUsd(v),
                                    })),
                                    {
                                        data: overview.daily.map(d => Math.max(0, d.totalUsd - Object.values(d.byWorkflow).reduce((a, b) => a + b, 0))),
                                        label: 'Other', stack: 'cost', valueFormatter: (v: number | null) => formatAiUsd(v),
                                    },
                                ]}
                                height={320}
                                margin={{ left: 80, right: 20, top: 60 }}
                            />
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}
